import { DEFAULT_CONFIRM_ACTION_MODAL } from 'assets/data';
import BaseButton from 'components/ui/BaseButton';
import Modal, {
  ModalContent,
  ModalFooter,
  ModalHeader
} from 'components/ui/Modal';
import { POST_HOG_EVENT } from 'constants/events';
import {
  LOCAL_STORAGE,
  MODAL_TYPES,
  OVERLAY_Z_INDEX_CLASS
} from 'constants/general';
import { ROUTES } from 'constants/routes';
import { FLAG, FOCUS_SUMMARY_MODAL_MODE } from 'enums';
import {
  ConfirmDeleteCustomRoutineType,
  ConfirmDeleteHabitType,
  ConfirmHabitPackInstallOrUninstall
} from 'interfaces/modal.interface';
import { useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { logout } from 'services/auth.service';
import { resetApiRateLimitState } from 'services/axios-config';
import { t } from 'services/i18n.service';
import {
  capturePostHogEvent,
  resetPostHogToAnonymous
} from 'services/posthog.service';
import { useAppDispatch, useAppSelectorShallow } from 'store/hooks';
import {
  deleteFocusTemplate,
  removeFocusTemplateFromMarketPlace
} from 'store/reducer/focus-mode/extra';
import { confirmActionModalSelector } from 'store/reducer/modal/confirm-action.modal.selectors';
import {
  resetTodoSubTasks,
  updateConfirmActionModal,
  updateFocusSummaryEdit,
  updateMoreOptionIncludeEveryBreak,
  updateMoreOptionIsOfficeFriendly
} from 'store/reducer/modal/slice';
import {
  deleteHabitPack,
  installHabitPack,
  removePackFromMarketplace,
  uninstallHabitPack
} from 'store/reducer/habit-pack/extra';
import {
  addFlag,
  removeCustomRoutine,
  removeHabit
} from 'store/reducer/setting/slice';
import { deleteNote } from 'store/reducer/note/extra';
import {
  deleteKeyword,
  deleteToDo,
  refreshTodosAllStatuses
} from 'store/reducer/to-do/extra';
import {
  updateIsDeletingTask,
  updatePlayerIsReadyToSendUpdates,
  updatePlayerSelectedTask,
  updatePlayerTasks,
  updateSelectedTaskToTaskForm,
  updateShowNewTaskForm
} from 'store/reducer/to-do/slice';
import { deleteFocusBlockSummary } from 'store/reducer/user/extra';
import { updateIsUserLoggingOut } from 'store/reducer/user/slice';
import { removeLocalStorage } from 'utils/helpers/storage';
import LoadingIndicator from './LoadingIndicator';
import Container from 'components/ui/Container';

const ConfirmActionModal = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const {
    cancelTitle,
    confirmTitle,
    description,
    modalType,
    taskId,
    children,
    currentViewedFocusTemplate,
    isFocusTemplateDeleting,
    currentViewedPack,
    isHabitPacksDeleting,
    isUserUpsertingPack,
    areSettingsSaving,
    deleteKeywordModal: { id, platform },
    playerTasks,
    data,
    playerSelectedTask,
    userEmail,
    selectedTaskToTaskForm,
    isDeletingTask,
    isDeletingNote
  } = useAppSelectorShallow(confirmActionModalSelector);

  const isWaiting = useMemo(
    () =>
      isFocusTemplateDeleting ||
      isHabitPacksDeleting ||
      isUserUpsertingPack ||
      areSettingsSaving ||
      isDeletingTask ||
      isDeletingNote,
    [
      isFocusTemplateDeleting,
      isHabitPacksDeleting,
      isUserUpsertingPack,
      areSettingsSaving,
      isDeletingTask,
      isDeletingNote
    ]
  );

  const handleClose = useCallback(() => {
    dispatch(updateConfirmActionModal(DEFAULT_CONFIRM_ACTION_MODAL));
  }, [dispatch]);

  const handleConfirm = async () => {
    if (isWaiting) {
      return;
    }
    if (
      modalType === MODAL_TYPES.CONFIRM_ACTION_DELETE_FOCUS_TEMPLATE &&
      currentViewedFocusTemplate?.id
    ) {
      await dispatch(deleteFocusTemplate(currentViewedFocusTemplate.id));
      navigate(`../${ROUTES.MARKETPLACE}?filter=mine`);
    } else if (
      modalType === MODAL_TYPES.CONFIRM_ACTION_DELETE_HABIT_PACK &&
      currentViewedPack?.id
    ) {
      await dispatch(
        deleteHabitPack({
          packId: currentViewedPack.id,
          user_id: currentViewedPack.user_id
        })
      );
      navigate(`../${ROUTES.MARKETPLACE}?filter=mine`);
    } else if (
      modalType ===
        MODAL_TYPES.CONFIRM_ACTION_REMOVE_HABIT_PACK_FROM_MARKET_PLACE &&
      currentViewedPack
    ) {
      dispatch(removePackFromMarketplace(currentViewedPack));
    } else if (
      modalType ===
        MODAL_TYPES.CONFIRM_ACTION_REMOVE_FOCUS_TEMPLATE_FROM_MARKET_PLACE &&
      currentViewedFocusTemplate
    ) {
      dispatch(removeFocusTemplateFromMarketPlace(currentViewedFocusTemplate));
    } else if (modalType === MODAL_TYPES.CONFIRM_ACTION_LOGOUT) {
      dispatch(updateIsUserLoggingOut(true));
      resetPostHogToAnonymous();
      resetApiRateLimitState();
      removeLocalStorage([
        LOCAL_STORAGE.AUTH_BEARER_TOKEN,
        LOCAL_STORAGE.REDIRECT_PATH,
        LOCAL_STORAGE.SALES_IQ,
        LOCAL_STORAGE.AUTH_ID,
        LOCAL_STORAGE.AUTH_BEARER_REFRESH
      ]);
      logout();
    } else if (modalType === MODAL_TYPES.DELETE_KEYWORD_MODAL) {
      dispatch(deleteKeyword({ id, platform }));
    } else if (modalType === MODAL_TYPES.CONFIRM_ACTION_DELETE_PLAYER_TASK) {
      taskId === playerSelectedTask?.id &&
        dispatch(updatePlayerSelectedTask(undefined));
      dispatch(
        updatePlayerTasks(playerTasks.filter((task) => task.id !== taskId))
      );
      dispatch(updatePlayerIsReadyToSendUpdates(true));
    } else if (modalType === MODAL_TYPES.CONFIRM_ACTION_SAVE_HABITS) {
      dispatch(addFlag(FLAG.CONTINUE_SAVING_HABITS));
    } else if (modalType === MODAL_TYPES.CONFIRM_HABIT_DELETION && data) {
      const { type, position, customRoutinePosition } =
        data as ConfirmDeleteHabitType;
      dispatch(removeHabit({ type, position, customRoutinePosition }));
      userEmail &&
        capturePostHogEvent(POST_HOG_EVENT.DELETE_HABIT_CONFIRM, {
          'routine-type': type
        });
    } else if (
      modalType === MODAL_TYPES.CONFIRM_CUSTOM_ROUTINE_DELETION &&
      data
    ) {
      const { position } = data as ConfirmDeleteCustomRoutineType;
      dispatch(removeCustomRoutine(position));
    } else if (modalType === MODAL_TYPES.CONFIRM_HABIT_PACK_INSTALLATION) {
      const { packId } = data as ConfirmHabitPackInstallOrUninstall;
      dispatch(installHabitPack(packId));
    } else if (modalType === MODAL_TYPES.CONFIRM_HABIT_PACK_UNINSTALLATION) {
      const { packId } = data as ConfirmHabitPackInstallOrUninstall;
      dispatch(uninstallHabitPack(packId));
    } else if (modalType === MODAL_TYPES.CONFIRM_ACTION_DELETE_TASK) {
      const deletedTaskId = taskId || selectedTaskToTaskForm?.id;
      if (!deletedTaskId) {
        dispatch(updateConfirmActionModal(DEFAULT_CONFIRM_ACTION_MODAL));
        return;
      }
      dispatch(updateIsDeletingTask(true));
      const response = await dispatch(deleteToDo(deletedTaskId));
      if (response.meta.requestStatus === 'fulfilled') {
        toast.success(t('to_do_procrastinate.task_deleted_successfully'));
        refreshToDos(deletedTaskId);
      } else {
        // Failed delete must not touch local buckets or the open form —
        // the row stays put and the drawer keeps the unsaved task.
        toast.error(t('to_do_procrastinate.could_not_delete_the_task'));
      }
      dispatch(updateIsDeletingTask(false));
    } else if (modalType === MODAL_TYPES.CONFIRM_ACTION_DELETE_NOTE && taskId) {
      const result = await dispatch(deleteNote(taskId));
      if (result.payload !== true) {
        return;
      }
    } else if (modalType === MODAL_TYPES.CONFIRM_INCLUDE_IN_EVERY_BREAK) {
      const value = data as boolean;
      dispatch(updateMoreOptionIncludeEveryBreak(value));
      dispatch(updateMoreOptionIsOfficeFriendly(value));
    } else if (
      modalType === MODAL_TYPES.CONFIRM_ACTION_DELETE_FOCUS_BLOCK_SUMMARY
    ) {
      const value = data as { id: string } | undefined;
      if (value?.id) {
        try {
          await dispatch(deleteFocusBlockSummary(value.id)).unwrap();
          dispatch(
            updateFocusSummaryEdit({
              showFocusSummaryEdit: false,
              focusSummary: null,
              mode: FOCUS_SUMMARY_MODAL_MODE.EDIT
            })
          );
        } catch {
          // Error is already handled/toasted in the thunk
        }
      }
    }
    dispatch(updateConfirmActionModal(DEFAULT_CONFIRM_ACTION_MODAL));
  };

  const refreshToDos = (deletedTaskId?: string) => {
    // Close the form only when the deleted task was open in the drawer.
    // Deleting a different list-card task must not pop the drawer or
    // clobber unsaved edits.
    if (selectedTaskToTaskForm && selectedTaskToTaskForm.id === deletedTaskId) {
      dispatch(updateSelectedTaskToTaskForm(null));
      dispatch(updateShowNewTaskForm(false));
      dispatch(resetTodoSubTasks());
    }
    dispatch(refreshTodosAllStatuses());
  };

  return (
    <Modal
      isOpen
      onClose={handleClose}
      zIndex={OVERLAY_Z_INDEX_CLASS.MODAL_OVER_DRAWER}
      closeOnOverlayClick={!isWaiting}
      closeOnEscape={!isWaiting}>
      <ModalHeader onClose={isWaiting ? undefined : handleClose} />
      <ModalContent>
        <Container>
          {description ? (
            <h3 className='text-center font-medium'>{description}</h3>
          ) : null}
          {children}
          {isWaiting ? (
            <LoadingIndicator additionalStyles='mx-auto ~my-6/12' />
          ) : null}
        </Container>
      </ModalContent>
      {isWaiting ? null : (
        <ModalFooter>
          <BaseButton
            onClick={handleClose}
            title={cancelTitle ?? t('btn_cancel')}
            variant='secondary'
            disableDarkMode
            data-testid='modal-cancel-button'
          />
          <BaseButton
            title={confirmTitle}
            onClick={handleConfirm}
            disableDarkMode
            data-testid='modal-confirm-button'
          />
        </ModalFooter>
      )}
    </Modal>
  );
};

export default ConfirmActionModal;
